/**
 * SOUL MERIDIAN's signature motif: a single white line that runs across the
 * screen and, on the right, unravels into six undulating strands.
 *
 * One reusable SVG element serves both the title screen and the load screen:
 *  - "title" mode: the strands are fully unravelled and drift forever.
 *  - "loading" mode: `progress` (0..1) drives how far the strands emerge and
 *    spread, so the line literally unravels as assets load.
 *
 * Look: the left spine is pure white; the right strands take on soft, shifting
 * pastel hues, and every stroke is doubled by a blurred halo of its own colour.
 * The strands peel off the spine tangentially and bow gently outward, so the
 * split reads as a soft fray rather than a hard fork.
 *
 * `stir()` injects a decaying ripple — the title calls it on every state change
 * ("whenever the screen changes, the unravelled bit moves around a bit").
 */
const SVGNS = "http://www.w3.org/2000/svg";

export type MeridianMode = "title" | "loading";

/** Per-strand constants: undulation frequencies + phases and a base hue, fixed
 *  so the motion and colour drift are deterministic across reloads. */
interface StrandDef {
  off: number; // -1..1 vertical fan target at the right edge
  f1: number;
  f2: number;
  fe: number;
  p1: number;
  p2: number;
  pe: number;
  hue: number; // base hue (deg)
  hueRate: number;
}

const BASE_HUES = [188, 206, 258, 166, 282, 222];

function buildStrands(n: number): StrandDef[] {
  const half = (n - 1) / 2;
  const out: StrandDef[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      off: half === 0 ? 0 : (i - half) / half,
      f1: 0.42 + i * 0.121,
      f2: 0.31 + i * 0.093,
      fe: 0.24 + i * 0.107,
      p1: i * 1.7,
      p2: i * 2.3 + 1.1,
      pe: i * 0.9 + 0.5,
      hue: BASE_HUES[i % BASE_HUES.length],
      hueRate: 0.11 + i * 0.028,
    });
  }
  return out;
}

// Smoothstep: flat at both ends, so the strands sit fully open at the top and
// merge all the way to zero at the bottom (a clean single line at 100% load).
const smoothstep = (x: number): number => x * x * (3 - 2 * x);
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

export class MeridianLine {
  /** Container — position it (inset:0) in CSS; the SVG fills it. */
  readonly el: HTMLDivElement;

  private svg: SVGSVGElement;
  private spine: SVGPathElement;
  private spineHalo: SVGPathElement;
  private strandEls: SVGPathElement[] = [];
  private haloEls: SVGPathElement[] = [];
  private strands: StrandDef[];

  private readonly splitFrac: number;
  private readonly spreadFrac: number;
  private mode: MeridianMode = "title";
  private progress = 1; // eased target in title mode is 1
  private uShown = 1; // smoothed unravel amount actually drawn

  private stirE = 0; // amplitude boost, fast decay
  private phaseKick = 0; // time offset, slow decay

  private raf = 0;
  private t0 = 0;
  private w = 0;
  private h = 0;

  private ro: ResizeObserver | null = null;

  constructor(opts: { splitFrac?: number; strands?: number; spreadFrac?: number } = {}) {
    this.splitFrac = opts.splitFrac ?? 0.58;
    // How wide the fan opens, as a fraction of the element's height. The
    // default suits a full screen; a short strip (a panel's header rule) needs
    // a much larger fraction or the six strands collapse into one bright bar.
    this.spreadFrac = opts.spreadFrac ?? 0.19;
    this.strands = buildStrands(opts.strands ?? 6);

    this.el = document.createElement("div");
    this.el.className = "meridian";

    this.svg = document.createElementNS(SVGNS, "svg");
    this.svg.setAttribute("class", "meridian-svg");
    this.svg.setAttribute("preserveAspectRatio", "none");

    // A generous-region gaussian blur produces the coloured halo behind each line.
    const defs = document.createElementNS(SVGNS, "defs");
    const filter = document.createElementNS(SVGNS, "filter");
    filter.setAttribute("id", "meridian-glow");
    filter.setAttribute("x", "-50%");
    filter.setAttribute("y", "-200%");
    filter.setAttribute("width", "200%");
    filter.setAttribute("height", "500%");
    const blur = document.createElementNS(SVGNS, "feGaussianBlur");
    blur.setAttribute("stdDeviation", "3.5");
    filter.appendChild(blur);
    defs.appendChild(filter);
    this.svg.appendChild(defs);

    // Halo group (blurred), then the crisp core group on top.
    const haloG = document.createElementNS(SVGNS, "g");
    haloG.setAttribute("class", "meridian-halo");
    haloG.setAttribute("filter", "url(#meridian-glow)");
    const coreG = document.createElementNS(SVGNS, "g");
    coreG.setAttribute("class", "meridian-core");

    this.spineHalo = document.createElementNS(SVGNS, "path");
    this.spineHalo.setAttribute("class", "meridian-spine-halo");
    haloG.appendChild(this.spineHalo);
    this.spine = document.createElementNS(SVGNS, "path");
    this.spine.setAttribute("class", "meridian-spine");
    coreG.appendChild(this.spine);

    for (let i = 0; i < this.strands.length; i++) {
      const edge = Math.abs(this.strands[i].off);
      const halo = document.createElementNS(SVGNS, "path");
      halo.setAttribute("class", "meridian-strand-halo");
      halo.style.opacity = String(0.5 - edge * 0.18);
      haloG.appendChild(halo);
      this.haloEls.push(halo);

      const core = document.createElementNS(SVGNS, "path");
      core.setAttribute("class", "meridian-strand");
      core.style.opacity = String(0.96 - edge * 0.32);
      coreG.appendChild(core);
      this.strandEls.push(core);
    }

    this.svg.appendChild(haloG);
    this.svg.appendChild(coreG);
    this.el.appendChild(this.svg);

    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this.el);
    }
  }

  setMode(mode: MeridianMode): void {
    this.mode = mode;
    if (mode === "title") this.progress = 1;
  }

  /** Loading progress 0..1 (loading mode only). */
  setProgress(frac: number): void {
    this.progress = clamp01(frac);
  }

  /** Nudge the strands — a decaying ripple that settles back. */
  stir(amount = 1): void {
    this.stirE = Math.min(1.4, this.stirE + amount);
    this.phaseKick += 0.7 + amount * 0.7;
  }

  start(): void {
    if (this.raf) return;
    this.layout();
    this.t0 = performance.now();
    const tick = (now: number): void => {
      this.draw((now - this.t0) / 1000);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  dispose(): void {
    this.stop();
    this.ro?.disconnect();
    this.el.remove();
  }

  private layout(): void {
    const r = this.el.getBoundingClientRect();
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.svg.setAttribute("viewBox", `0 0 ${this.w} ${this.h}`);
  }

  private draw(t: number): void {
    if (!this.w) this.layout();
    const { w, h } = this;
    const midY = h / 2;
    const splitX = w * this.splitFrac;
    const rightX = w; // the motif always spans the full screen width
    const run = Math.max(1, rightX - splitX);
    const throat = run * 0.14; // short fray region where strands peel off one by one

    // "Openness": how far the strands are unravelled. The title stays fully open
    // forever; while LOADING they begin open and CONVERGE onto the spine as
    // progress → 1, merging into a single straight line (which then fades).
    const targetOpen = this.mode === "loading" ? 1 - this.progress : 1;
    this.uShown += (targetOpen - this.uShown) * 0.12;
    const open = smoothstep(clamp01(this.uShown));

    // Decays: amplitude ripple settles fast, the phase shift lingers.
    this.stirE *= 0.94;
    this.phaseKick *= 0.975;
    const wob = 1 + this.stirE * 1.7;
    const kick = this.phaseKick;

    const maxSpread = h * this.spreadFrac;
    const spread = maxSpread * open;
    const reachX = rightX; // edge-to-edge at every progress
    const baseAmp = h * this.spreadFrac * 0.16 * open; // straightens flat as it converges

    // The spine runs on through the throat so the strands appear to lift off a
    // continuous line rather than burst from a single hard point.
    const spineEnd = splitX + throat;
    const spineD = `M 0 ${midY.toFixed(1)} L ${spineEnd.toFixed(1)} ${midY.toFixed(1)}`;
    this.spine.setAttribute("d", spineD);
    this.spineHalo.setAttribute("d", spineD);

    for (let i = 0; i < this.strands.length; i++) {
      const s = this.strands[i];
      const len = 0.6 + 0.7 * Math.abs(s.off); // outer strands swing wider
      const a1 = baseAmp * len;
      const a2 = baseAmp * 1.3 * len;
      const ae = baseAmp * 1.1 * len;

      // Peel off the spine near-tangentially, then bow outward: the first
      // control point sits close to the split and only slightly off-axis, so
      // the six strands emerge as a soft fray instead of radiating from a point.
      // Each strand peels off at its own point along the throat (inner strands
      // originate deeper), leaving nearly tangent to the spine and only bowing
      // outward later — a soft fray instead of a hard fork. The near-origin
      // control point barely wobbles so the departure stays calm.
      const originX = splitX + (1 - Math.abs(s.off)) * throat;
      const seg = Math.max(1, reachX - originX);
      const c1x = originX + seg * 0.32;
      const c1y = midY + s.off * spread * 0.07 + Math.sin(t * s.f1 + s.p1 + kick) * a1 * 0.5 * wob;
      const c2x = originX + seg * 0.62;
      const c2y = midY + s.off * spread * 0.8 + Math.sin(t * s.f2 + s.p2 + kick) * a2 * wob;
      const endY = midY + s.off * spread + Math.sin(t * s.fe + s.pe + kick) * ae * wob;

      const d =
        `M ${originX.toFixed(1)} ${midY.toFixed(1)} ` +
        `C ${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ` +
        `${reachX.toFixed(1)} ${endY.toFixed(1)}`;
      this.strandEls[i].setAttribute("d", d);
      this.haloEls[i].setAttribute("d", d);

      // Soft, shifting pastel hue that fades toward pure white as the strands
      // merge into the single main line (open → 0).
      const hue = s.hue + Math.sin(t * s.hueRate + i) * 18;
      const coreSat = (62 * open).toFixed(0);
      const coreLit = (82 + 18 * (1 - open)).toFixed(0);
      const haloSat = (85 * open).toFixed(0);
      const haloLit = (66 + 30 * (1 - open)).toFixed(0);
      this.strandEls[i].setAttribute("stroke", `hsl(${hue.toFixed(0)} ${coreSat}% ${coreLit}%)`);
      this.haloEls[i].setAttribute("stroke", `hsl(${hue.toFixed(0)} ${haloSat}% ${haloLit}%)`);
    }
  }
}
